/**
 * The "Ask about this" bridge — dashboard cell → existing chat.
 *
 * Deliberately one-way and text-only: it composes a question and hands it to
 * `/chat` as a prefilled draft. It does not create a chat, does not send
 * anything, and knows nothing about chat internals; the user still reads the
 * question and presses send.
 */

import type { KpiCell, KpiPeriodGrain, KpiUnit } from "../../types/kpi";
import { formatKpiValueExact, formatPeriod } from "./formatKpiValue";

export interface KpiQuestionContext {
	/** Property name, or null on the portfolio rollup. */
	propertyKey: string | null;
	metricLabel: string;
	period: string;
	/** The table's grain — the draft asks about "Q4 2023", not "Oct 2023". */
	grain?: string;
	unit: KpiUnit;
	cell: KpiCell | undefined;
}

/**
 * The prefilled question. It states the value and where it came from, because
 * the chat has no access to what the user was looking at — without the source
 * the assistant would have to re-find the number before it could discuss it.
 */
export function buildKpiQuestion(context: KpiQuestionContext): string {
	const { propertyKey, metricLabel, period, grain, unit, cell } = context;
	const scope = propertyKey ?? "the portfolio";
	const periodLabel = formatPeriod(period, grain ?? "month");

	if (!cell || cell.effective_value === null) {
		return `For ${scope}, ${metricLabel} has no value for ${periodLabel}. Which source file should it come from, and why might it be missing?`;
	}

	const value = formatKpiValueExact(cell.effective_value, unit);
	const source = cell.source;
	const where = source
		? ` It came from ${source.file}${source.sheet ? `, sheet "${source.sheet}"` : ""}${
				source.ref ? `, cell ${source.ref}` : ""
			}${source.page ? `, page ${source.page}` : ""}.`
		: cell.derived
			? " It is calculated from other extracted values."
			: "";

	return `For ${scope}, ${metricLabel} in ${periodLabel} is ${value}.${where} Can you explain this number and how it moved?`;
}

/** The chat URL that opens with the question already in the input. */
export function kpiChatUrl(question: string): string {
	return `/chat?prefill=${encodeURIComponent(question)}`;
}

/**
 * What the user is looking at, as the Analyst rail sends it with every turn
 * (`message_metadata.dashboard_context`, DOP-437). A descriptor, never data:
 * the server rebuilds the grid from it for the chat's owner, so nothing here
 * becomes a fact the agent reads.
 */
export interface DashboardContext {
	kind: "kpi_dashboard";
	/**
	 * The dashboard the question was asked ON (workflow public id), so the
	 * thread has a page to be reopened at — `chatHref` routes a `kpi_dashboard`
	 * thread to `/workflows/kpi/:id` only when `surface_state` carries this,
	 * and falls back to the index without it (DOP-478 decision 5).
	 *
	 * Absent on a dashboard reached without a workflow, which is the honest
	 * answer: there is no page for a thread that was not asked of one.
	 */
	workflow_id?: string;
	scope: { property_key: string | null; rollup: "portfolio" | null };
	grain: KpiPeriodGrain;
	period_from: string | null;
	period_to: string | null;
	hide_empty_rows: boolean;
	/** The cell whose evidence is open, so "this number" means one number. */
	selected: { metric_key: string; period: string } | null;
}

export interface DashboardContextInput {
	/** The dashboard's workflow public id, when the surface has one. */
	workflowId?: string;
	/** Property on screen, or null on the portfolio rollup. */
	propertyKey: string | null;
	/** The grain the numbers on screen are in — the server's, not the request. */
	grain: KpiPeriodGrain;
	hideEmptyRows: boolean;
	selected: { metricKey: string; period: string } | null;
}

export function buildDashboardContext(
	input: DashboardContextInput,
): DashboardContext {
	return {
		kind: "kpi_dashboard",
		...(input.workflowId ? { workflow_id: input.workflowId } : {}),
		scope:
			input.propertyKey === null
				? { property_key: null, rollup: "portfolio" }
				: { property_key: input.propertyKey, rollup: null },
		grain: input.grain,
		period_from: null,
		period_to: null,
		hide_empty_rows: input.hideEmptyRows,
		selected: input.selected
			? { metric_key: input.selected.metricKey, period: input.selected.period }
			: null,
	};
}
